import { TimeZoneType } from "../../constants/timezones"
import { Status } from "../../types/common"

export const makeProjectDashboardViewModel = (
  projectDashboard: ProjectDashboardResponse
): ProjectDashboardViewModel => {
  const {
    plannedStartDate,
    plannedEndDate,
    actualStartDate,
    actualEndDate,
    allCompletedTasks,
    allInProgressTasks,
    allNotStartedTasks,
    allRejectedTasks,
    completedOnTimeTasks,
    completedLateTasks,
    completedOtherTasks,
    notCompletedOnTimeTasks,
    notCompletedPastStartTasks,
    notCompletedLateTasks,
    notCompletedOtherTasks,
  } = projectDashboard

  const numberOfCompletedTasks = allCompletedTasks.length
  const numberOfInProgressTasks = allInProgressTasks.length
  const numberOfNotStartedTasks = allNotStartedTasks.length
  const numberOfRejectedTasks = allRejectedTasks.length
  const numberOfCompletedOnTimeTasks = completedOnTimeTasks.length
  const numberOfCompletedLateTasks = completedLateTasks.length
  const numberOfCompletedOtherTasks = completedOtherTasks.length
  const numberOfNotCompletedOnTimeTasks = notCompletedOnTimeTasks.length
  const numberOfNotCompletedPastStartTasks = notCompletedPastStartTasks.length
  const numberOfNotCompletedLateTasks = notCompletedLateTasks.length
  const numberOfNotCompletedOtherTasks = notCompletedOtherTasks.length
  const numberOfNotCompletedTasks =
    numberOfNotCompletedOnTimeTasks +
    numberOfNotCompletedPastStartTasks +
    numberOfNotCompletedLateTasks +
    numberOfNotCompletedOtherTasks

  let numberOfDaysUntilPlannedStart = 0
  if (plannedStartDate) {
    const plannedStartDateInMs = new Date(plannedStartDate).getTime()
    const currentDateInMs = new Date().getTime()
    const differenceInTime = plannedStartDateInMs - currentDateInMs
    numberOfDaysUntilPlannedStart = Math.floor(differenceInTime / (1000 * 3600 * 24))
  }

  let numberOfDaysFromPlannedStart = 0
  if (plannedStartDate) {
    const plannedStartDateInMs = new Date(plannedStartDate).getTime()
    const currentDateInMs = new Date().getTime()
    const differenceInTime = currentDateInMs - plannedStartDateInMs
    numberOfDaysFromPlannedStart = Math.floor(differenceInTime / (1000 * 3600 * 24))
  }

  let numberOfDaysProjectStartedLate = 0
  if (plannedStartDate && actualStartDate) {
    const plannedStartDateInMs = new Date(plannedStartDate).getTime()
    const actualStartDateInMs = new Date(actualStartDate).getTime()
    const differenceInTime = actualStartDateInMs - plannedStartDateInMs
    numberOfDaysProjectStartedLate = Math.floor(differenceInTime / (1000 * 3600 * 24))
  }

  let numberOfDaysFromPlannedCompletion = 0
  if (plannedEndDate) {
    const plannedEndDateInMs = new Date(plannedEndDate).getTime()
    const currentDateInMs = new Date().getTime()
    const differenceInTime = currentDateInMs - plannedEndDateInMs
    numberOfDaysFromPlannedCompletion = Math.floor(differenceInTime / (1000 * 3600 * 24))
  }

  let numberOfDaysUntilPlannedCompletion = 0
  if (plannedEndDate) {
    const plannedEndDateInMs = new Date(plannedEndDate).getTime()
    const currentDateInMs = new Date().getTime()
    const differenceInTime = plannedEndDateInMs - currentDateInMs
    numberOfDaysUntilPlannedCompletion = Math.floor(differenceInTime / (1000 * 3600 * 24))
  }

  let numberOfDaysProjectCompletedLate = 0
  if (plannedEndDate && actualEndDate) {
    const plannedEndDateInMs = new Date(plannedEndDate).getTime()
    const actualEndDateInMs = new Date(actualEndDate).getTime()
    const differenceInTime = actualEndDateInMs - plannedEndDateInMs
    numberOfDaysProjectCompletedLate = Math.floor(differenceInTime / (1000 * 3600 * 24))
  }

  let delayPercentage = 0
  if (plannedStartDate && plannedEndDate && actualStartDate && actualEndDate) {
    const projectDurationInMs = new Date(plannedEndDate).getTime() - new Date(plannedStartDate).getTime()
    const delayDurationInMs = new Date(actualEndDate).getTime() - new Date(plannedEndDate).getTime()
    delayPercentage = (delayDurationInMs / projectDurationInMs) * 100
  }

  return {
    ...projectDashboard,
    numberOfCompletedTasks,
    numberOfInProgressTasks,
    numberOfNotStartedTasks,
    numberOfRejectedTasks,
    numberOfDaysUntilPlannedStart,
    numberOfDaysFromPlannedStart,
    numberOfDaysProjectStartedLate,
    numberOfDaysFromPlannedCompletion,
    numberOfDaysUntilPlannedCompletion,
    numberOfDaysProjectCompletedLate,
    delayPercentage,
    numberOfCompletedOnTimeTasks,
    numberOfCompletedLateTasks,
    numberOfCompletedOtherTasks,
    numberOfNotCompletedTasks,
    numberOfNotCompletedOnTimeTasks,
    numberOfNotCompletedPastStartTasks,
    numberOfNotCompletedLateTasks,
    numberOfNotCompletedOtherTasks,
  }
}

export type ProjectDashboardViewModel = ProjectDashboardResponse & {
  numberOfNotStartedTasks: number
  numberOfInProgressTasks: number
  numberOfCompletedTasks: number
  numberOfRejectedTasks: number
  numberOfDaysUntilPlannedStart: number
  numberOfDaysFromPlannedStart: number
  numberOfDaysProjectStartedLate: number
  numberOfDaysFromPlannedCompletion: number
  numberOfDaysUntilPlannedCompletion: number
  numberOfDaysProjectCompletedLate: number
  delayPercentage: number
  numberOfCompletedOnTimeTasks: number
  numberOfCompletedLateTasks: number
  numberOfCompletedOtherTasks: number
  numberOfNotCompletedTasks: number
  numberOfNotCompletedOnTimeTasks: number
  numberOfNotCompletedPastStartTasks: number
  numberOfNotCompletedLateTasks: number
  numberOfNotCompletedOtherTasks: number
}

export type ProjectDashboardResponse = {
  id: string
  projectNumber: string
  projectTitle: string
  projectStatus: Status
  latestShortStatusDescription: string | null
  latestShortStatusDate: string | null
  plannedStartDate: string
  plannedEndDate: string
  actualStartDate: string
  actualEndDate: string
  projectCompletion: number
  completionPercentageBasedOnTasks: number
  projectCustomers: string[]
  projectManagers: string[]
  projectParticipants: string[]
  projectSuppliers: string[]
  projectWorkspaces: string[]
  taskResources: string[]
  taskSuppliers: string[]
  taskWorkspaces: string[]
  totalNumberOfTasks: number
  allCompletedTasks: string[]
  allInProgressTasks: string[]
  allNotStartedTasks: string[]
  allRejectedTasks: string[]
  groupedNotStartedTasks: GroupedTasksBasedOnStatus
  groupedInProgressTasks: GroupedTasksBasedOnStatus
  groupedCompletedTasks: GroupedTasksBasedOnStatus
  groupedRejectedTasks: GroupedTasksBasedOnStatus
  scheduleConsumptionPercentage: number
  rejectedDate: string | null
  timeZone: TimeZoneType
  enableTimeComponent: boolean
  completedOnTimeTasks: string[]
  completedLateTasks: string[]
  completedOtherTasks: string[]
  notCompletedOnTimeTasks: string[]
  notCompletedPastStartTasks: string[]
  notCompletedLateTasks: string[]
  notCompletedOtherTasks: string[]
  tasksBasedOnResources: {
    resourceName: string
    groupedNotStartedTasks: GroupedTasksBasedOnStatus
    groupedInProgressTasks: GroupedTasksBasedOnStatus
    groupedCompletedTasks: GroupedTasksBasedOnStatus
  }[]
  isPersonalProject: boolean
  isOrgProject: boolean
}

type GroupedTasksBasedOnStatus = {
  [key: string]: {
    tasks: string[]
    numberOfTasks: number
    percentageOfTasks: number
  }
}
